import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { downloadBlob } from '@/utils/download-file-from-link';
import { getInfiniteQueryListNextParam } from '@/utils/list';
import { toast } from '@/utils/toast';

import {
  attachEquipmentDocuments,
  createEquipment,
  deleteEquipment,
  deleteEquipmentCrewNotes,
  deleteEquipmentDocument,
  getAllEquipment,
  getEquipmentCategories,
  getEquipmentPdf,
  getEquipments,
  getEquipmentTypeFields,
  getEquipmentTypes,
  getSingleEquipment,
  setEquipmentCrewNotes,
  updateEquipment,
  updateEquipmentFacts,
} from './equipment.api';
import type { EquipmentFactsPatch } from './equipment.mappers';
import type { CreateUpdateEquipmentFormFields, SingleEquipment } from './equipment.types';

export const useEquipmentCategoriesQuery = (limit: number, search: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: ['equipment-categories-list', limit, search],
    queryFn: ({ pageParam }) => getEquipmentCategories(pageParam, limit, search),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
    enabled,
  });

/**
 * Vessel binds `category_id` into a `*string` and filters on whatever it holds,
 * so asking with a blank one asks for the types of the category named '' and is
 * answered with none. The guard spares that request to a caller still resolving
 * which category it wants.
 */
export const useEquipmentTypesQuery = (categoryId: string) =>
  useQuery({
    queryKey: ['equipment-types-list', categoryId],
    queryFn: () => getEquipmentTypes(categoryId),
    enabled: !!categoryId,
  });

export const useEquipmentsQuery = (
  categoryId: string,
  limit: number,
  search: string,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: ['equipment-list', categoryId, limit, search],
    queryFn: ({ pageParam }) => getEquipments(categoryId, pageParam, limit, search),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
    enabled: enabled && !!categoryId,
  });

/**
 * Every item on the vessel, cached once for the whole session's screens.
 *
 * Separate from `useEquipmentsQuery` rather than a special case of it: that one is the
 * category browser's own paged list, keyed by category, search and page size, so a
 * caller that just needs to resolve an id would either miss the cache or pollute it.
 */
export const useAllEquipmentQuery = (enabled = true) =>
  useQuery({
    queryKey: ['equipment-all'],
    queryFn: getAllEquipment,
    // Held rather than always asked for: with the default `staleTime` of 0 a cached
    // answer is stale the moment it lands, so a second observer mounting later — a
    // table that appears only once its rows arrive — refetches on mount. One observer
    // per screen is what keeps that to one request.
    enabled,
  });

export const useSingleEquipmentQuery = (id?: string) =>
  useQuery({
    queryKey: ['single-equipment', id],
    queryFn: () => getSingleEquipment(id!),
    enabled: !!id,
  });

export const useEquipmentTypeFieldsQuery = (equipmentTypeId?: null | string) =>
  useQuery({
    queryKey: ['equipment-type-fields', equipmentTypeId],
    queryFn: () => getEquipmentTypeFields(equipmentTypeId!),
    enabled: !!equipmentTypeId,
  });

export const useUpdateEquipmentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: CreateUpdateEquipmentFormFields }) =>
      updateEquipment(id, values),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['single-equipment', id] });
      queryClient.invalidateQueries({ queryKey: ['equipment-list'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-all'] });
    },
  });
};

/**
 * The install date and the hour meter, edited from the item's page. It writes
 * through the item's own PUT, so the list's copy of the item goes stale with it.
 */
export const useUpdateEquipmentFactsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      equipment,
      patch,
    }: {
      equipment: SingleEquipment;
      patch: EquipmentFactsPatch;
    }) => updateEquipmentFacts(equipment, patch),
    onSuccess(_, { equipment }) {
      queryClient.invalidateQueries({ queryKey: ['single-equipment', equipment.id] });
      queryClient.invalidateQueries({ queryKey: ['equipment-list'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-all'] });
    },
  });
};

export const useCreateEquipmentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateUpdateEquipmentFormFields) => createEquipment(values),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['equipment-list'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-all'] });
      // The category carries its own `equipment_count`, so the grid the crew
      // comes back to would otherwise still show the tally from before the add.
      queryClient.invalidateQueries({ queryKey: ['equipment-categories-list'] });
    },
  });
};

export const useDeleteEquipmentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEquipment(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['equipment-list'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-all'] });
    },
  });
};

/** The item carries its documents, so the item's own query is what goes stale. */
export const useAttachEquipmentDocumentsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) =>
      attachEquipmentDocuments(id, files),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['single-equipment', id] });
    },
  });
};

export const useDeleteEquipmentDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documentId }: { id: string; documentId: string }) =>
      deleteEquipmentDocument(id, documentId),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['single-equipment', id] });
    },
  });
};

/** Crew notes ride on the item, so the item's own query is what goes stale. */
export const useSetEquipmentCrewNotesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, crewNotes }: { id: string; crewNotes: string }) =>
      setEquipmentCrewNotes(id, crewNotes),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['single-equipment', id] });
    },
  });
};

export const useDeleteEquipmentCrewNotesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEquipmentCrewNotes(id),
    onSuccess(_, id) {
      queryClient.invalidateQueries({ queryKey: ['single-equipment', id] });
    },
  });
};

/**
 * The item's PDF report. Asking for it leaves nothing on the page stale, so the
 * mutation only fetches the file and hands it to the browser — named after the
 * item so a folder of them stays readable.
 */
export const useEquipmentPdfMutation = () =>
  useMutation({
    mutationFn: async ({
      id,
      name,
      timezone,
    }: {
      id: string;
      name?: string;
      timezone?: string;
    }) => {
      const blob = await getEquipmentPdf(id, timezone);
      await downloadBlob(blob, `equipment-${name || id}.pdf`);
    },
    onError() {
      toast.error('Couldn’t generate the equipment report. Please try again.');
    },
  });
